import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Avatar, Box, Button, Chip, LinearProgress, Radio, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { BLUE, BORDER, ERROR, ERROR_SOFT, ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, primaryButtonSx, PROGRESS_TRACK, SLATE, SOFT_BLUE, SUCCESS_SOFT, SUCCESS_TEXT, SURFACE_MUTED, SURFACE_TINT, WARNING, WARNING_SOFT, WARNING_TEXT } from '../teacherStyles';
import { initialSpeakingAttempts } from './LessonPageData';
import { SectionCard } from './ClassPageUi';

type SoundGrade = 'good' | 'ok' | 'bad';
type AlignmentGrade = 'speech' | 'extra' | 'silence';
type SoundGradeKey = 'initial' | 'final' | 'tone';
type SoundGradeItem = { character: string; pinyinExpected: string; pinyinSpoken: string; initial: SoundGrade; final: SoundGrade; tone: SoundGrade; alignment: AlignmentGrade };

const soundGradeColor: Record<SoundGrade, string> = { good: GREEN, ok: WARNING, bad: ERROR };
const soundGradeTextColor: Record<SoundGrade, string> = { good: SUCCESS_TEXT, ok: WARNING_TEXT, bad: ERROR_TEXT };
const soundGradeLabel: Record<SoundGrade, string> = { good: 'Good', ok: 'Close', bad: 'Needs practice' };
const soundGradeMark: Record<SoundGrade, string> = { good: '✓', ok: '~', bad: '!' };
const soundGradeOrder: SoundGrade[] = ['good', 'ok', 'bad'];

const initialSoundGrades = Object.fromEntries(
    initialSpeakingAttempts.map(attempt => [attempt.id, attempt.pronunciation.map(item => ({ ...item })) as SoundGradeItem[]]),
) as Record<string, SoundGradeItem[]>;

function TinySoundGrade({ value, label, onChange }: { value: SoundGrade; label: string; onChange: (grade: SoundGrade) => void }) {
    const clickTimerRef = React.useRef<number | null>(null);
    const longTimerRef = React.useRef<number | null>(null);
    const longFiredRef = React.useRef(false);

    const clearClick = () => {
        if (clickTimerRef.current !== null) window.clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
    };
    const clearLong = () => {
        if (longTimerRef.current !== null) window.clearTimeout(longTimerRef.current);
        longTimerRef.current = null;
    };

    return (
        <Box
            role="button"
            tabIndex={0}
            aria-label={`${label}: ${soundGradeLabel[value]}. Use arrow keys to change the grade, or G, O, and B to choose a grade directly.`}
            onPointerDown={() => {
                longFiredRef.current = false;
                clearLong();
                longTimerRef.current = window.setTimeout(() => {
                    longFiredRef.current = true;
                    clearClick();
                    onChange('bad');
                }, 600);
            }}
            onPointerUp={clearLong}
            onPointerLeave={clearLong}
            onClick={() => {
                if (longFiredRef.current) return;
                clearClick();
                clickTimerRef.current = window.setTimeout(() => {
                    onChange('good');
                    clearClick();
                }, 220);
            }}
            onDoubleClick={() => {
                clearClick();
                onChange('ok');
            }}
            onKeyDown={event => {
                const directGrade = event.key.toLowerCase() === 'g' ? 'good' : event.key.toLowerCase() === 'o' ? 'ok' : event.key.toLowerCase() === 'b' ? 'bad' : null;
                if (directGrade) {
                    event.preventDefault();
                    onChange(directGrade);
                    return;
                }
                if (event.key === 'Home' || event.key === 'End') {
                    event.preventDefault();
                    onChange(event.key === 'Home' ? 'good' : 'bad');
                    return;
                }
                if (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
                    const nextIndex = (soundGradeOrder.indexOf(value) + direction + soundGradeOrder.length) % soundGradeOrder.length;
                    onChange(soundGradeOrder[nextIndex]);
                }
            }}
            sx={{ width: 40, height: 40, borderRadius: '10px', cursor: 'pointer', outlineOffset: 2, backgroundColor: `${soundGradeColor[value]}33`, display: 'grid', placeItems: 'center' }}
        >
            <Typography aria-hidden="true" sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 17, lineHeight: 1, color: soundGradeTextColor[value] }}>
                {soundGradeMark[value]}
            </Typography>
        </Box>
    );
}

export default function LessonSpeakingReviewPage({ onAction }: { onAction: (message: string) => void }) {
    const [attempts, setAttempts] = React.useState(initialSpeakingAttempts);
    const [selectedId, setSelectedId] = React.useState(initialSpeakingAttempts[0].id);
    const [playingId, setPlayingId] = React.useState<string | null>(null);
    const [soundGrades, setSoundGrades] = React.useState<Record<string, SoundGradeItem[]>>(initialSoundGrades);
    const selectedAttempt = attempts.find(attempt => attempt.id === selectedId) ?? attempts[0];
    const awaitingCount = attempts.filter(attempt => attempt.status === 'Awaiting review').length;

    const selectAttempt = (attemptId: string) => {
        setSelectedId(attemptId);
    };

    const playRecording = () => {
        setPlayingId(selectedAttempt.id);
        onAction(`Playing ${selectedAttempt.student}'s recording in this static demo.`);
        window.setTimeout(() => setPlayingId(null), 1400);
    };

    const markReviewed = (message: string) => {
        setAttempts(current => current.map(attempt => attempt.id === selectedAttempt.id ? { ...attempt, status: 'Reviewed' } : attempt));
        onAction(message);
    };

    const updateSoundGrade = (index: number, key: SoundGradeKey, value: SoundGrade) => {
        setSoundGrades(current => {
            const attemptGrades = current[selectedAttempt.id].map(item => ({ ...item }));
            attemptGrades[index] = { ...attemptGrades[index], [key]: value, alignment: 'speech' };
            return { ...current, [selectedAttempt.id]: attemptGrades };
        });
    };

    const updateAlignment = (index: number, value: AlignmentGrade) => {
        setSoundGrades(current => {
            const attemptGrades = current[selectedAttempt.id].map(item => ({ ...item }));
            attemptGrades[index] = value === 'silence'
                ? { ...attemptGrades[index], initial: 'bad', final: 'bad', tone: 'bad', alignment: value }
                : { ...attemptGrades[index], alignment: value };
            return { ...current, [selectedAttempt.id]: attemptGrades };
        });
    };

    const markNothingSpoken = () => {
        setSoundGrades(current => {
            const attemptGrades = current[selectedAttempt.id].map(item => ({ ...item, initial: 'bad' as SoundGrade, final: 'bad' as SoundGrade, tone: 'bad' as SoundGrade, alignment: 'silence' as AlignmentGrade }));
            return { ...current, [selectedAttempt.id]: attemptGrades };
        });
    };

    const openNext = () => {
        const currentIndex = attempts.findIndex(attempt => attempt.id === selectedAttempt.id);
        const nextAttempt = attempts.slice(currentIndex + 1).find(attempt => attempt.status === 'Awaiting review') ?? attempts.find(attempt => attempt.status === 'Awaiting review');
        if (nextAttempt) selectAttempt(nextAttempt.id);
        else onAction('All speaking attempts in this lesson have been reviewed.');
    };

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '320px minmax(0, 1fr)' }, gap: 2.5, alignItems: 'start' }}>
            <SectionCard sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 2.25, borderBottom: `1px solid ${BORDER}` }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Review queue</Typography>
                        <Chip label={`${awaitingCount} awaiting`} size="small" sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: awaitingCount ? ERROR_TEXT : SUCCESS_TEXT, backgroundColor: awaitingCount ? ERROR_SOFT : SUCCESS_SOFT }} />
                    </Stack>
                </Box>
                {attempts.map((attempt, index) => (
                    <Button
                        key={attempt.id}
                        fullWidth
                        onClick={() => selectAttempt(attempt.id)}
                        sx={{ justifyContent: 'flex-start', px: 2, py: 1.5, borderRadius: 0, borderTop: index === 0 ? 0 : `1px solid ${BORDER}`, textAlign: 'left', textTransform: 'none', backgroundColor: attempt.id === selectedAttempt.id ? SOFT_BLUE : '#fff', '&:hover': { backgroundColor: attempt.id === selectedAttempt.id ? SOFT_BLUE : SURFACE_TINT } }}
                    >
                        <Avatar sx={{ width: 34, height: 34, mr: 1.25, backgroundColor: attempt.id === selectedAttempt.id ? BLUE : NAVY, fontFamily: POPPINS, fontSize: 11 }}>{attempt.initials}</Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY }}>{attempt.student}</Typography>
                            <Typography noWrap sx={{ fontFamily: INTER, fontSize: 11, color: SLATE, mt: 0.15 }}>{attempt.exercise} · AI {attempt.aiScore}</Typography>
                        </Box>
                        {attempt.status === 'Reviewed' && <CheckCircleRoundedIcon sx={{ ml: 1, color: GREEN, fontSize: 19 }} />}
                    </Button>
                ))}
            </SectionCard>

            <Stack spacing={2.5}>
                <SectionCard sx={{ overflow: 'hidden' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                        <Box>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>{selectedAttempt.student}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>{selectedAttempt.exercise} · Submitted {selectedAttempt.submitted}</Typography>
                        </Box>
                        <Chip label={selectedAttempt.status} size="small" sx={{ alignSelf: { sm: 'flex-start' }, fontFamily: INTER, fontWeight: 700, color: selectedAttempt.status === 'Reviewed' ? SUCCESS_TEXT : WARNING_TEXT, backgroundColor: selectedAttempt.status === 'Reviewed' ? SUCCESS_SOFT : WARNING_SOFT }} />
                    </Stack>

                    <Box sx={{ p: 2.5 }}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: SLATE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Prompt</Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 13.5, lineHeight: 1.55, color: NAVY, mt: 0.5 }}>{selectedAttempt.prompt}</Typography>
                        <Box sx={{ mt: 1.5, p: 1.75, borderRadius: '14px', backgroundColor: SURFACE_MUTED }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: SLATE }}>Expected response</Typography>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, mt: 0.35 }}>{selectedAttempt.expected}</Typography>
                        </Box>

                        <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} onClick={playRecording} sx={{ ...primaryButtonSx, mt: 2, borderRadius: '11px', minWidth: 180 }}>
                            {playingId === selectedAttempt.id ? 'Playing recording…' : 'Play recording'}
                        </Button>
                        {playingId === selectedAttempt.id && <LinearProgress sx={{ mt: 1, maxWidth: 360, height: 5, borderRadius: 3, backgroundColor: PROGRESS_TRACK, '& .MuiLinearProgress-bar': { backgroundColor: BLUE } }} />}
                    </Box>
                </SectionCard>

                <SectionCard sx={{ p: 2.5, overflow: 'hidden' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                        <Box>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Individual sound grading</Typography>
                        </Box>
                        <Chip label="Manual grade" size="small" sx={{ alignSelf: { sm: 'flex-start' }, fontFamily: INTER, fontWeight: 700, color: BLUE, backgroundColor: SOFT_BLUE }} />
                    </Stack>

                    <Box sx={{ overflowX: 'auto', mt: 2, pb: 1 }}>
                        <Box
                            data-testid="teacher-sound-grading-grid"
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: `118px repeat(${soundGrades[selectedAttempt.id].length}, 48px)`,
                                columnGap: 0.75,
                                rowGap: 0.75,
                                alignItems: 'center',
                                minWidth: 'max-content',
                            }}
                        >
                            <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>Target</Typography>
                            {soundGrades[selectedAttempt.id].map((item, index) => (
                                <Typography key={`pinyin-${index}`} align="center" sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: NAVY }}>{item.pinyinExpected}</Typography>
                            ))}

                            <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>Heard</Typography>
                            {soundGrades[selectedAttempt.id].map((item, index) => {
                                const matchesTarget = item.pinyinSpoken === item.pinyinExpected;
                                return (
                                    <Typography
                                        key={`spoken-${index}`}
                                        align="center"
                                        sx={{
                                            px: 0.35,
                                            py: 0.4,
                                            borderRadius: '8px',
                                            fontFamily: INTER,
                                            fontSize: 11,
                                            fontWeight: matchesTarget ? 400 : 700,
                                            color: matchesTarget ? SLATE : ERROR_TEXT,
                                            backgroundColor: matchesTarget ? PROGRESS_TRACK : WARNING_SOFT,
                                        }}
                                    >
                                        {item.pinyinSpoken}
                                    </Typography>
                                );
                            })}

                            <Box />
                            {soundGrades[selectedAttempt.id].map((item, index) => {
                                const combinedGrade: SoundGrade = item.initial === 'bad' || item.final === 'bad' || item.tone === 'bad' ? 'bad' : item.initial === 'ok' || item.final === 'ok' || item.tone === 'ok' ? 'ok' : 'good';
                                return (
                                    <Box key={`character-${index}`} sx={{ height: 38, display: 'grid', placeItems: 'center', borderRadius: '8px', backgroundColor: item.character === '-' || item.alignment === 'silence' ? PROGRESS_TRACK : `${soundGradeColor[combinedGrade]}26` }}>
                                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 21, color: NAVY }}>{item.character}</Typography>
                                    </Box>
                                );
                            })}

                            {(['initial', 'final', 'tone'] as SoundGradeKey[]).map(key => (
                                <React.Fragment key={key}>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: NAVY, textTransform: 'capitalize' }}>{key}</Typography>
                                    {soundGrades[selectedAttempt.id].map((item, index) => (
                                        <Box key={`${key}-${index}`} sx={{ display: 'grid', placeItems: 'center' }}>
                                            {item.character === '-'
                                                ? <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: PROGRESS_TRACK }} />
                                                : <TinySoundGrade value={item[key]} label={`${item.character} ${key}`} onChange={grade => updateSoundGrade(index, key, grade)} />}
                                        </Box>
                                    ))}
                                </React.Fragment>
                            ))}

                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: NAVY }}>Speech</Typography>
                            {soundGrades[selectedAttempt.id].map((item, index) => (
                                item.character === '-'
                                    ? <Box key={`speech-${index}`} sx={{ height: 30 }} />
                                    : <Radio key={`speech-${index}`} size="small" checked={item.alignment === 'speech'} onChange={() => updateAlignment(index, 'speech')} inputProps={{ 'aria-label': `${item.character}: speech` }} sx={{ p: 0.5, justifySelf: 'center', color: BLUE }} />
                            ))}

                            <Tooltip title="Student spoke an extra character" arrow placement="left">
                                <Stack direction="row" alignItems="center" spacing={0.4}>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: NAVY }}>Extra speech</Typography>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: BLUE }} />
                                </Stack>
                            </Tooltip>
                            {soundGrades[selectedAttempt.id].map((item, index) => (
                                item.character === '-'
                                    ? <Radio key={`extra-${index}`} size="small" checked={item.alignment === 'extra'} onChange={() => updateAlignment(index, 'extra')} inputProps={{ 'aria-label': `Extra speech: ${item.pinyinSpoken}` }} sx={{ p: 0.5, justifySelf: 'center', color: BLUE }} />
                                    : <Box key={`extra-${index}`} sx={{ height: 30 }} />
                            ))}

                            <Tooltip title="Student did not speak this character" arrow placement="left">
                                <Stack direction="row" alignItems="center" spacing={0.4}>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: NAVY }}>Silence / noise</Typography>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: BLUE }} />
                                </Stack>
                            </Tooltip>
                            {soundGrades[selectedAttempt.id].map((item, index) => (
                                <Radio key={`silence-${index}`} size="small" checked={item.alignment === 'silence'} onChange={() => updateAlignment(index, 'silence')} inputProps={{ 'aria-label': `${item.character}: silence or noise` }} sx={{ p: 0.5, justifySelf: 'center', color: BLUE }} />
                            ))}
                        </Box>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mt: 1.25 }}>
                        <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE }}><strong>Pointer:</strong> click Good, double-click Close, long-press Needs practice.&nbsp; <strong>Keyboard:</strong> arrows cycle; G/O/B select directly.</Typography>
                        <Stack direction="row" spacing={1}>
                            <Button onClick={markNothingSpoken} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: BLUE, textTransform: 'none' }}>No words were spoken</Button>
                            <Button variant="contained" onClick={() => markReviewed(`Individual grades saved for ${selectedAttempt.student} in this static demo.`)} sx={{ ...primaryButtonSx, borderRadius: '10px', px: 2 }}>Save</Button>
                            <Button endIcon={<NavigateNextRoundedIcon />} onClick={openNext} sx={{ color: BLUE, fontFamily: INTER, fontWeight: 700 }}>Next</Button>
                        </Stack>
                    </Stack>
                </SectionCard>
            </Stack>
        </Box>
    );
}
